import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type Membership } from '../api';
import { PageHeader } from '../components/PageHeader';
import { orgPath, workspacePath, type NavOrg } from '../nav';
import { useSession } from '../session';
import { OpenPrompt, OrgTile, useOpenSession, type Openable } from '../openSession';

/** "3 hours ago" beats a timestamp here: what matters is whether it was this session or last month. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 90) return 'just now';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)} minutes ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Enough to recognise a workspace by; the rest is one click away on its own page. */
const ORGS_SHOWN = 5;

export function Home() {
  const { me } = useSession();
  // Null until the first answer, so "nothing recent yet" is not shown to somebody whose list is loading.
  const [recent, setRecent] = useState<Openable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { open, busy, prompt, dismissPrompt } = useOpenSession(setError);

  const load = useCallback(async () => {
    try {
      const r = await api<{ orgs: Openable[] }>('/api/me/recent-orgs');
      setRecent(r.orgs);
    } catch (err) {
      setRecent([]);
      setError(err instanceof ApiError ? err.message : 'Could not load your recent orgs');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!me) return null;

  const firstName = me.user.displayName ? me.user.displayName.split(' ')[0] : '';
  const names = new Map(me.memberships.map((m) => [m.tenantId, m.name]));
  const hasRecent = recent !== null && recent.length > 0;

  return (
    <>
      <PageHeader
        title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
        actions={
          <Link to="/open" className="button secondary">
            Every org you can open
          </Link>
        }
      />

      {error && <div className="alert">{error}</div>}
      {me.openMfaReset && (
        <div className="notice">
          A sign-in reset request is open for your account. Your access to customer orgs is suspended until an
          owner decides it.
        </div>
      )}

      {prompt && (
        <OpenPrompt
          prompt={prompt}
          onSubmit={(extra) => void open(prompt.org, extra)}
          onCancel={dismissPrompt}
        />
      )}

      {/* The whole point of the home page: somebody works in two or three orgs this week, and the rest of
          the estate is a list they should not have to read to reach one of them. */}
      {hasRecent && (
        <section>
          <h2 style={{ marginTop: 0 }}>Pick up where you left off</h2>
          <div className="tiles">
            {recent.map((o) => (
              <OrgTile
                key={o.customerOrgId + o.identityId}
                org={o}
                busy={busy}
                onOpen={() => void open(o)}
                note={o.lastOpenedAt ? `Opened ${ago(o.lastOpenedAt)}` : undefined}
                // Only worth saying when there is more than one workspace it could be in.
                workspace={me.memberships.length > 1 ? names.get(o.tenantId) : undefined}
              />
            ))}
          </div>
        </section>
      )}

      {/* The way in for everything else: the company first, then the Salesforce orgs it connects. Each
          tile lists its orgs so the second step is visible from the first, and links straight to them. */}
      <section>
        <h2 style={hasRecent ? undefined : { marginTop: 0 }}>Your workspaces</h2>
        <p className="lede">
          A workspace is your company in Kruvi Access: its people, and the Salesforce orgs it connects to. Open
          one to manage who has access, or go straight to one of its orgs.
        </p>
        {me.memberships.length === 0 ? (
          <div className="notice">You are not in a workspace yet. Ask an owner to invite you.</div>
        ) : (
          <div className="tiles">
            {me.memberships.map((m) => (
              <WorkspaceTile key={m.tenantId} membership={m} />
            ))}
          </div>
        )}
      </section>

      {recent !== null && recent.length === 0 && (
        <p className="small muted" style={{ marginTop: 18 }}>
          Orgs you open will appear at the top of this page. <Link to="/open">See what you can open</Link>.
        </p>
      )}
    </>
  );
}

/**
 * One workspace and the Salesforce orgs in it.
 *
 * Each tile asks for its own orgs, so a slow or failing workspace says so in its own tile and the rest of
 * the page, including the recent orgs above, is not held up waiting for it. For a member the server
 * answers with only the orgs they hold a grant on, which is the list they can act on anyway.
 */
function WorkspaceTile({ membership }: { membership: Membership }) {
  const { tenantId, name, role } = membership;
  const canManage = role === 'owner' || role === 'admin';
  const [orgs, setOrgs] = useState<NavOrg[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Ignore an answer that arrives after the tile has gone, or after it was asked again.
    let live = true;
    api<{ orgs: NavOrg[] }>(`/api/tenants/${tenantId}/orgs`)
      .then((r) => {
        if (live) setOrgs(r.orgs);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'Could not load its Salesforce orgs');
      });
    return () => {
      live = false;
    };
  }, [tenantId]);

  const shown = orgs?.slice(0, ORGS_SHOWN) ?? [];
  const more = orgs ? orgs.length - shown.length : 0;

  return (
    <div className="tile">
      <div className="tile-title">
        <Link to={workspacePath(tenantId)}>{name}</Link>
      </div>
      <div className="tile-meta">
        <span className="pill">{role}</span>
        <span>Workspace</span>
      </div>

      <div className="tile-section">
        <div className="tile-section-head">Salesforce orgs</div>
        {error && <div className="tile-error">{error}</div>}
        {!error && orgs === null && <div className="small muted">Loading…</div>}
        {orgs?.length === 0 && (
          <div className="small muted">{canManage ? 'None connected yet.' : 'You hold no grants here yet.'}</div>
        )}
        {shown.length > 0 && (
          <ul className="tile-orgs">
            {shown.map((o) => (
              <li key={o.id}>
                <Link to={orgPath(tenantId, o.id)}>
                  <span className="tile-org-label">{o.label}</span>
                  {o.status !== 'active' && (
                    <span className={`dot ${o.status}`} title={o.status} role="img" aria-label={o.status} />
                  )}
                  <span className={`env-tag ${o.environment}`}>{o.environment === 'sandbox' ? 'Sandbox' : 'Production'}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {more > 0 && (
          <Link to={workspacePath(tenantId)} className="small">
            and {more} more
          </Link>
        )}
      </div>

      <div className="actions">
        <Link to={workspacePath(tenantId)} className="button block">
          Open workspace
        </Link>
        {canManage && orgs?.length === 0 && (
          <Link to={`${workspacePath(tenantId)}/orgs/new`} className="button secondary block">
            Connect an org
          </Link>
        )}
      </div>
    </div>
  );
}
